import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, ChevronRight, ChevronLeft, Lock, CheckCircle2, Download, Mail } from "lucide-react";
import api, { formatApiError } from "../api";
import { useAuth } from "../auth";
import { Logo, LogoLight } from "../components/Logo";

/*
 * OUVRIR UNE SUCCESSION — parcours 4 étapes + diagnostic gratuit + feuille de route payée
 * Palette teal/laiton via .succession-theme (index.css)
 */

const OUI_NON_INCONNU = [
  { v: "oui", lbl: "Oui" },
  { v: "non", lbl: "Non" },
  { v: "je ne sais pas", lbl: "Je ne sais pas" },
];
const OUI_NON = [
  { v: "oui", lbl: "Oui" },
  { v: "non", lbl: "Non" },
];
const OUI_NON_NC = [
  { v: "oui", lbl: "Oui" },
  { v: "non", lbl: "Non" },
  { v: "non concerné", lbl: "Non concerné" },
];
const CADASTRE_OPTS = [
  { v: "oui", lbl: "Oui" },
  { v: "partiellement", lbl: "Partiellement" },
  { v: "non", lbl: "Non" },
  { v: "je ne sais pas", lbl: "Je ne sais pas" },
];
const LIENS_ARBRE = [
  "Le défunt", "Conjoint(e) survivant(e)", "Enfant", "Petit-enfant", "Frère/Sœur", "Autre héritier",
];
const TYPES_BIENS = ["Appartement", "Maison", "Immeuble", "Terrain", "Autre"];

const STEPS = [
  { key: 0, label: "Ma situation" },
  { key: 1, label: "Arbre généalogique" },
  { key: 2, label: "Mes documents" },
  { key: 3, label: "Mon rapport" },
];

const emptySituation = () => ({
  defunt_prenom: "", defunt_nom: "", territoire: "", ville: "", date_deces: "",
  testament_connu: "", avait_enfants: "", enfants: [],
  notaire_designe: "", notaire_info: { nom_etude: "", adresse: "", telephone: "", email: "" },
  nombre_heritiers: "", tous_identifies: "", heritiers: [],
  diaspora: "", introuvables: "",
  bien_immobilier: "", indivision: "", cadastre_connu: "", biens_immobiliers: [],
  patrimoine_uniquement_immo: "",
  desaccord: "", motifs_desaccord: [],
});

export { OUI_NON_INCONNU, OUI_NON, OUI_NON_NC, CADASTRE_OPTS, LIENS_ARBRE, TYPES_BIENS, STEPS, emptySituation };

export default function Succession() {
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [config, setConfig] = useState(null);
  const [succession, setSuccession] = useState(null);
  const [situation, setSituation] = useState(emptySituation());
  const [arbre, setArbre] = useState([]);
  const [documents, setDocuments] = useState({});
  const [diagnostic, setDiagnostic] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [paymentUrl, setPaymentUrl] = useState("");
  const [resendStatus, setResendStatus] = useState("");
  const [pollingPayment, setPollingPayment] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined") document.title = "Ouvrir une succession · AUPRÉA";
  }, []);

  // Load config + user's succession dossier
  useEffect(() => {
    (async () => {
      try {
        const [{ data: cfg }, { data: me }] = await Promise.all([
          api.get("/succession/config"),
          api.get("/succession/mine"),
        ]);
        setConfig(cfg);
        setSuccession(me);
        if (me.situation && Object.keys(me.situation).length) {
          setSituation({ ...emptySituation(), ...me.situation });
        }
        setArbre(me.arbre || []);
        setDocuments(me.documents_checklist || {});
        if (me.paid) {
          // load roadmap
          try {
            const { data: rm } = await api.get("/succession/mine/roadmap");
            setRoadmap(rm);
            setStep(3);
          } catch (_e) { /* ignore */ }
        }
      } catch (e) {
        setErr(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Polling paid status when we return from Stripe
  const pollPaid = useCallback(async () => {
    setPollingPayment(true);
    try {
      for (let i = 0; i < 20; i++) {
        const { data } = await api.get("/succession/mine");
        if (data.paid) {
          setSuccession(data);
          const { data: rm } = await api.get("/succession/mine/roadmap");
          setRoadmap(rm);
          setResendStatus("Paiement confirmé — feuille de route débloquée !");
          return;
        }
        await new Promise((r) => setTimeout(r, 3000));
      }
      setResendStatus("Paiement en cours de traitement. Rechargez la page dans quelques minutes si votre feuille de route n'apparaît pas.");
    } finally {
      setPollingPayment(false);
    }
  }, []);

  const goNext = async () => {
    if (step === 3) return;
    setSaving(true);
    setErr("");
    try {
      if (step === 0) {
        const { data } = await api.patch("/succession/mine/situation", situation);
        setSuccession(data);
      } else if (step === 1) {
        const { data } = await api.patch("/succession/mine/arbre", { arbre });
        setSuccession(data);
      } else if (step === 2) {
        const { data } = await api.patch("/succession/mine/documents", { documents_checklist: documents });
        setSuccession(data);
        // Recharger le diagnostic pour l'étape 4
        const { data: diag } = await api.get("/succession/mine/diagnostic");
        setDiagnostic(diag);
      }
      setStep(step + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const goPrev = () => {
    if (step === 0) return;
    setStep(step - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openPayment = async () => {
    try {
      const { data } = await api.get("/succession/mine/payment-link");
      setPaymentUrl(data.url);
      window.open(data.url, "_blank", "noopener,noreferrer");
      pollPaid();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  const resendPDF = async () => {
    setResendStatus("Envoi en cours…");
    try {
      await api.post("/succession/mine/resend-pdf");
      setResendStatus("Feuille de route renvoyée par e-mail à " + (user?.email || "vous"));
    } catch (e) {
      setResendStatus("Erreur : " + formatApiError(e));
    }
  };

  const downloadPDF = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/succession/mine/pdf`;
    window.open(url, "_blank");
  };

  if (loading) {
    return (
      <div className="succession-theme" style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "var(--succ-muted)" }}>Chargement de votre dossier…</div>
      </div>
    );
  }

  return (
    <div className="succession-theme" style={{ minHeight: "100vh" }} data-testid="succession-page">
      <SuccHeader />
      <ProgressStrip step={step} paid={!!succession?.paid} />
      <main style={{ maxWidth: 900, margin: "0 auto", padding: "32px 20px 60px" }}>
        {err && (
          <div style={{ background: "rgba(181,58,58,0.08)", border: "1px solid rgba(181,58,58,0.3)", color: "#b53a3a", padding: "12px 16px", borderRadius: 4, marginBottom: 20, fontSize: 14 }} data-testid="succ-error">
            {err}
          </div>
        )}
        {step === 0 && <StepSituation situation={situation} setSituation={setSituation} config={config} />}
        {step === 1 && <StepArbre arbre={arbre} setArbre={setArbre} config={config} />}
        {step === 2 && <StepDocuments documents={documents} setDocuments={setDocuments} config={config} />}
        {step === 3 && (
          <StepRapport
            succession={succession}
            diagnostic={diagnostic}
            roadmap={roadmap}
            config={config}
            openPayment={openPayment}
            downloadPDF={downloadPDF}
            resendPDF={resendPDF}
            resendStatus={resendStatus}
            pollingPayment={pollingPayment}
            onRefreshPaid={pollPaid}
          />
        )}
        {step < 3 && (
          <div className="flex items-center justify-between mt-10 gap-3">
            {step > 0 ? (
              <button onClick={goPrev} className="succ-btn-outline" data-testid="btn-prev">
                <ChevronLeft size={16} style={{ marginRight: 6, display: "inline" }} /> Précédent
              </button>
            ) : <span />}
            <button
              onClick={goNext}
              disabled={saving}
              className="succ-btn-primary"
              data-testid={`btn-next-${step}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              {saving ? "Enregistrement…" : (step === 2 ? "Voir le résultat de mon diagnostic →" : "Passer à l'étape suivante →")}
            </button>
          </div>
        )}
      </main>
      <SuccFooter />
    </div>
  );
}

function SuccHeader() {
  return (
    <header style={{ background: "var(--succ-teal-dark)", padding: "16px 24px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", display: "inline-flex" }}>
          <LogoLight size={26} />
        </Link>
        <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
          <Link to="/choix-espace" style={{ color: "rgba(239,241,236,0.75)", textDecoration: "none" }} data-testid="succ-switch-space">Changer d'espace</Link>
          <Link to="/tableau-de-bord" style={{ color: "rgba(239,241,236,0.75)", textDecoration: "none" }}>Mon espace</Link>
        </div>
      </div>
    </header>
  );
}

function SuccFooter() {
  return (
    <footer style={{ background: "var(--succ-teal-dark)", color: "rgba(239,241,236,0.7)", padding: "18px 24px", textAlign: "center", fontSize: 12 }}>
      <em>« Anticiper est un acte d'amour. »</em> — AUPRÉA SAS · Grand-Bourg, Guadeloupe · contact@auprea.com
    </footer>
  );
}

function ProgressStrip({ step, paid }) {
  return (
    <div style={{ background: "#fff", borderBottom: "1px solid rgba(31,94,86,0.1)", padding: "16px 20px" }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }} data-testid="succ-progress">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          {STEPS.map((s, i) => {
            const done = i < step;
            const active = i === step;
            const unlocked = paid && i === 3;
            return (
              <div
                key={s.key}
                data-testid={`step-indicator-${i}`}
                style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "6px 14px",
                  border: `1px solid ${active ? "var(--succ-teal)" : done ? "var(--succ-brass)" : "rgba(31,94,86,0.2)"}`,
                  background: active ? "var(--succ-teal)" : done ? "rgba(173,124,51,0.1)" : "transparent",
                  color: active ? "#EFF1EC" : done ? "var(--succ-brass-dark)" : "var(--succ-muted)",
                  borderRadius: 999, fontSize: 13, fontWeight: 600,
                }}
              >
                <span style={{ width: 20, height: 20, borderRadius: "50%", background: active ? "#EFF1EC" : done ? "var(--succ-brass)" : "rgba(31,94,86,0.15)", color: active ? "var(--succ-teal)" : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                  {done ? "✓" : i + 1}
                </span>
                {s.label}
                {unlocked && <span style={{ color: "var(--succ-brass)" }}>✦</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ---------- ÉTAPE 1 : Situation ---------- */
export function StepSituation({ situation, setSituation, config }) {
  const set = (k, v) => setSituation((s) => ({ ...s, [k]: v }));
  const setNotaire = (k, v) => setSituation((s) => ({ ...s, notaire_info: { ...s.notaire_info, [k]: v } }));
  return (
    <div data-testid="step-situation">
      <SectionHeader n={1} title="Ma situation" subtitle="Décrivez la situation actuelle, sans stress. Toutes les questions ne sont pas obligatoires : renseignez ce que vous savez." />

      {/* Le défunt */}
      <FieldGroup title="Le défunt et le décès">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F label="Prénom du défunt"><input className="succ-input" value={situation.defunt_prenom} onChange={(e) => set("defunt_prenom", e.target.value)} data-testid="input-defunt-prenom" /></F>
          <F label="Nom du défunt"><input className="succ-input" value={situation.defunt_nom} onChange={(e) => set("defunt_nom", e.target.value)} data-testid="input-defunt-nom" /></F>
          <F label="Territoire de résidence principale">
            <select className="succ-input" value={situation.territoire} onChange={(e) => set("territoire", e.target.value)} data-testid="input-territoire">
              <option value="">— Choisir —</option>
              {(config?.territoires || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </F>
          <F label="Ville ou code postal"><input className="succ-input" value={situation.ville} onChange={(e) => set("ville", e.target.value)} placeholder="Ex. 97110 ou Pointe-à-Pitre" data-testid="input-ville" /></F>
          <F label="Date du décès"><input className="succ-input" type="date" value={situation.date_deces} onChange={(e) => set("date_deces", e.target.value)} data-testid="input-date-deces" /></F>
        </div>
        <RadioLine label="Un testament est-il connu ?" value={situation.testament_connu} options={OUI_NON_INCONNU} onChange={(v) => set("testament_connu", v)} testid="q-testament" />
        <RadioLine label="Le défunt avait-il des enfants ?" value={situation.avait_enfants} options={OUI_NON_INCONNU} onChange={(v) => set("avait_enfants", v)} testid="q-enfants" />
        {situation.avait_enfants === "oui" && (
          <EnfantsList enfants={situation.enfants} setEnfants={(fn) => setSituation((s) => ({ ...s, enfants: typeof fn === "function" ? fn(s.enfants) : fn }))} />
        )}
        <RadioLine label="Un notaire a-t-il déjà été désigné ?" value={situation.notaire_designe} options={OUI_NON_INCONNU} onChange={(v) => set("notaire_designe", v)} testid="q-notaire" />
        {situation.notaire_designe === "oui" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <F label="Nom de l'étude"><input className="succ-input" value={situation.notaire_info.nom_etude || ""} onChange={(e) => setNotaire("nom_etude", e.target.value)} data-testid="input-notaire-etude" /></F>
            <F label="Adresse"><input className="succ-input" value={situation.notaire_info.adresse || ""} onChange={(e) => setNotaire("adresse", e.target.value)} data-testid="input-notaire-adresse" /></F>
            <F label="Téléphone"><input className="succ-input" value={situation.notaire_info.telephone || ""} onChange={(e) => setNotaire("telephone", e.target.value)} data-testid="input-notaire-tel" /></F>
            <F label="Email"><input className="succ-input" value={situation.notaire_info.email || ""} onChange={(e) => setNotaire("email", e.target.value)} data-testid="input-notaire-email" /></F>
          </div>
        )}
      </FieldGroup>

      {/* Héritiers */}
      <FieldGroup title="Les héritiers">
        <F label="Nombre d'héritiers potentiels (tous liens confondus)"><input className="succ-input" value={situation.nombre_heritiers} onChange={(e) => set("nombre_heritiers", e.target.value)} data-testid="input-nb-heritiers" /></F>
        <RadioLine label="Tous sont-ils identifiés et joignables ?" value={situation.tous_identifies} options={OUI_NON} onChange={(v) => set("tous_identifies", v)} testid="q-tous-identifies" />
        {situation.tous_identifies === "oui" && (
          <HeritiersList heritiers={situation.heritiers} setHeritiers={(fn) => setSituation((s) => ({ ...s, heritiers: typeof fn === "function" ? fn(s.heritiers) : fn }))} />
        )}
        <RadioLine label="Un ou plusieurs héritiers résident-ils hors du territoire du défunt (diaspora) ?" value={situation.diaspora} options={OUI_NON_INCONNU} onChange={(v) => set("diaspora", v)} testid="q-diaspora" />
        <RadioLine label="Un ou plusieurs héritiers sont-ils introuvables ou d'identité incertaine ?" value={situation.introuvables} options={OUI_NON_INCONNU} onChange={(v) => set("introuvables", v)} testid="q-introuvables" />
      </FieldGroup>

      {/* Patrimoine */}
      <FieldGroup title="Le patrimoine">
        <RadioLine label="Un bien immobilier fait-il partie de la succession ?" value={situation.bien_immobilier} options={OUI_NON} onChange={(v) => set("bien_immobilier", v)} testid="q-bien-immo" />
        {situation.bien_immobilier === "oui" && (
          <>
            <RadioLine label="Le bien doit-il être partagé entre plusieurs héritiers (indivision) ?" value={situation.indivision} options={OUI_NON_NC} onChange={(v) => set("indivision", v)} testid="q-indivision" />
            <RadioLine label="Connaissance des références cadastrales (numéros de lot) ?" value={situation.cadastre_connu} options={CADASTRE_OPTS} onChange={(v) => set("cadastre_connu", v)} testid="q-cadastre" />
            <BiensList biens={situation.biens_immobiliers} setBiens={(fn) => setSituation((s) => ({ ...s, biens_immobiliers: typeof fn === "function" ? fn(s.biens_immobiliers) : fn }))} />
          </>
        )}
        <RadioLine label="Le patrimoine repose-t-il uniquement sur des biens immobiliers et du foncier ?" value={situation.patrimoine_uniquement_immo} options={OUI_NON_INCONNU} onChange={(v) => set("patrimoine_uniquement_immo", v)} testid="q-patrimoine-uniquement-immo" />
      </FieldGroup>

      {/* Climat familial */}
      <FieldGroup title="Le climat familial">
        <RadioLine label="Y a-t-il des désaccords entre héritiers sur le partage ?" value={situation.desaccord} options={OUI_NON} onChange={(v) => set("desaccord", v)} testid="q-desaccord" />
        {situation.desaccord === "oui" && (
          <MotifsCheckboxes motifs={config?.motifs_desaccord || []} selected={situation.motifs_desaccord} onChange={(v) => set("motifs_desaccord", v)} />
        )}
      </FieldGroup>
    </div>
  );
}

/* ---------- ÉTAPE 2 : Arbre généalogique ---------- */
export function StepArbre({ arbre, setArbre, config }) {
  const [draft, setDraft] = useState({ prenom: "", nom: "", lien: "", statut: "vivant", ville_residence: "", documents_en_possession: [] });
  const availableDocs = (config?.documents || []).filter((d) => !(d.only_defunt && draft.lien !== "Le défunt"));

  const add = () => {
    if (!draft.prenom.trim() && !draft.nom.trim()) return;
    setArbre((a) => [...a, { ...draft }]);
    setDraft({ prenom: "", nom: "", lien: "", statut: "vivant", ville_residence: "", documents_en_possession: [] });
  };
  const remove = (idx) => setArbre((a) => a.filter((_, i) => i !== idx));

  const toggleDoc = (k) =>
    setDraft((d) => ({
      ...d,
      documents_en_possession: d.documents_en_possession.includes(k)
        ? d.documents_en_possession.filter((x) => x !== k)
        : [...d.documents_en_possession, k],
    }));

  return (
    <div data-testid="step-arbre">
      <SectionHeader n={2} title="Arbre généalogique" subtitle="Ajoutez les personnes concernées par la succession (défunt, conjoint, enfants, petits-enfants, autres héritiers connus)." />

      <div className="succ-card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Prénom"><input className="succ-input" value={draft.prenom} onChange={(e) => setDraft({ ...draft, prenom: e.target.value })} data-testid="input-arbre-prenom" /></F>
          <F label="Nom"><input className="succ-input" value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} data-testid="input-arbre-nom" /></F>
          <F label="Lien avec le défunt">
            <select className="succ-input" value={draft.lien} onChange={(e) => setDraft({ ...draft, lien: e.target.value })} data-testid="input-arbre-lien">
              <option value="">— Choisir —</option>
              {LIENS_ARBRE.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </F>
          <F label="Statut">
            <select className="succ-input" value={draft.statut} onChange={(e) => setDraft({ ...draft, statut: e.target.value })} data-testid="input-arbre-statut">
              <option value="vivant">Vivant</option>
              <option value="decede">Décédé(e)</option>
            </select>
          </F>
          <F label="Ville / pays de résidence"><input className="succ-input" value={draft.ville_residence} onChange={(e) => setDraft({ ...draft, ville_residence: e.target.value })} data-testid="input-arbre-ville" /></F>
        </div>
        <div className="mt-4">
          <label className="succ-label">Documents déjà en possession pour cette personne</label>
          <div className="flex flex-wrap gap-2">
            {availableDocs.map((d) => {
              const sel = draft.documents_en_possession.includes(d.key);
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => toggleDoc(d.key)}
                  data-testid={`doc-check-${d.key}`}
                  style={{
                    padding: "6px 12px",
                    border: `1px solid ${sel ? "var(--succ-teal)" : "var(--succ-line)"}`,
                    background: sel ? "rgba(31,94,86,0.1)" : "#fff",
                    color: sel ? "var(--succ-teal-dark)" : "var(--succ-muted)",
                    borderRadius: 4, fontSize: 12, cursor: "pointer",
                    fontWeight: sel ? 600 : 500,
                  }}
                >
                  {sel ? "✓ " : ""}{d.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mt-4">
          <button onClick={add} className="succ-btn-primary" data-testid="btn-arbre-add">
            <Plus size={14} style={{ display: "inline", marginRight: 6 }} /> Ajouter à l'arbre
          </button>
        </div>
      </div>

      <div data-testid="arbre-list">
        {arbre.length === 0 ? (
          <div style={{ color: "var(--succ-muted)", fontStyle: "italic", textAlign: "center", padding: 20 }}>
            Aucune personne ajoutée pour le moment.
          </div>
        ) : (
          arbre.map((p, i) => (
            <div key={i} className="succ-card mb-2" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }} data-testid={`arbre-person-${i}`}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: "var(--succ-teal-dark)" }}>{p.prenom} {p.nom}</div>
                <div style={{ fontSize: 13, color: "var(--succ-muted)" }}>{p.lien} · {p.statut === "vivant" ? "Vivant" : "Décédé(e)"}{p.ville_residence ? " · " + p.ville_residence : ""}</div>
                {(p.documents_en_possession || []).length > 0 && (
                  <div style={{ fontSize: 12, color: "var(--succ-brass-dark)", marginTop: 4 }}>Documents : {p.documents_en_possession.map((k) => (config?.documents || []).find((d) => d.key === k)?.label || k).join(", ")}</div>
                )}
              </div>
              <button onClick={() => remove(i)} style={{ background: "none", border: "none", color: "#b53a3a", cursor: "pointer" }} data-testid={`arbre-remove-${i}`}><Trash2 size={16} /></button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/* ---------- ÉTAPE 3 : Documents ---------- */
export function StepDocuments({ documents, setDocuments, config }) {
  const docs = (config?.documents || []).filter((d) => !d.only_defunt); // testament déjà rattaché au défunt via arbre
  const set = (k, v) => setDocuments({ ...documents, [k]: v });
  return (
    <div data-testid="step-documents">
      <SectionHeader n={3} title="Mes documents" subtitle="Pour chaque document ci-dessous, indiquez si vous l'avez déjà, s'il est à obtenir, ou si vous ne savez pas." />
      <div className="succ-card">
        {docs.map((d) => (
          <div key={d.key} style={{ borderBottom: "1px solid var(--succ-line)", padding: "12px 0" }} data-testid={`doc-row-${d.key}`}>
            <div style={{ fontWeight: 500, color: "var(--succ-ink)", marginBottom: 8 }}>{d.label}</div>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: "possession", lbl: "En ma possession", color: "var(--succ-teal)" },
                { v: "a_obtenir", lbl: "À obtenir", color: "var(--succ-brass)" },
                { v: "inconnu", lbl: "Je ne sais pas", color: "var(--succ-muted)" },
              ].map((o) => {
                const sel = documents[d.key] === o.v;
                return (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => set(d.key, o.v)}
                    data-testid={`doc-status-${d.key}-${o.v}`}
                    style={{
                      padding: "6px 14px",
                      border: `1px solid ${sel ? o.color : "var(--succ-line)"}`,
                      background: sel ? o.color : "#fff",
                      color: sel ? "#fff" : "var(--succ-ink)",
                      borderRadius: 4, fontSize: 13, cursor: "pointer",
                      fontWeight: sel ? 600 : 500,
                    }}
                  >
                    {o.lbl}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- ÉTAPE 4 : Rapport (diagnostic gratuit + roadmap payée) ---------- */
function StepRapport({ succession, diagnostic, roadmap, config, openPayment, downloadPDF, resendPDF, resendStatus, pollingPayment, onRefreshPaid }) {
  const paid = !!succession?.paid;
  const diag = roadmap || diagnostic || {};
  const tagMeta = config?.tag_meta || {};
  const proMeta = config?.professions || {};
  const price = config?.price_eur || 89;
  const situation = succession?.situation || {};
  const defunt = `${situation.defunt_prenom || ""} ${situation.defunt_nom || ""}`.trim() || "—";

  return (
    <div data-testid="step-rapport">
      <SectionHeader n={4} title="Mon rapport" subtitle="Votre diagnostic gratuit est prêt. Débloquez ci-dessous la feuille de route complète (professionnels, documents, actions)." />

      {/* Bloc gratuit */}
      <div className="succ-card mb-6" data-testid="diagnostic-libre">
        <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Diagnostic général — gratuit</div>
        <h2 style={{ margin: "0 0 12px 0", fontSize: 24 }}>Succession de {defunt}</h2>
        <p style={{ color: "var(--succ-ink)", lineHeight: 1.6, fontSize: 15, marginBottom: 16 }}>{diag.resume}</p>

        {(diag.tags || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {diag.tags.map((t) => {
              const meta = (diag.tag_meta || tagMeta)[t] || {};
              return (
                <span key={t} style={{ background: meta.color || "var(--succ-teal)", color: "#fff", padding: "5px 14px", borderRadius: 999, fontSize: 12, fontWeight: 600, letterSpacing: "0.03em" }} data-testid={`tag-${t}`}>
                  {meta.label || t}
                </span>
              );
            })}
          </div>
        )}
        {(diag.tags || []).length > 0 && (diag.tag_meta || tagMeta) && (
          <ul style={{ paddingLeft: 20, margin: 0, fontSize: 14, color: "var(--succ-muted)", lineHeight: 1.65 }}>
            {diag.tags.map((t) => {
              const meta = (diag.tag_meta || tagMeta)[t] || {};
              return <li key={t}><strong style={{ color: "var(--succ-teal-dark)" }}>{meta.label}</strong> — {meta.message}</li>;
            })}
          </ul>
        )}

        {(diag.professions_recommended || []).length > 0 && (
          <div className="mt-6">
            <div className="succ-label mb-2">Professionnels potentiellement concernés</div>
            <div className="flex flex-wrap gap-2">
              {diag.professions_recommended.map((p) => {
                const m = (diag.professions_meta || proMeta)[p] || {};
                return (
                  <span key={p} style={{ background: "rgba(31,94,86,0.08)", color: "var(--succ-teal-dark)", padding: "6px 12px", borderRadius: 4, fontSize: 13, border: "1px solid rgba(31,94,86,0.2)" }} data-testid={`pro-chip-${p}`}>
                    {m.icon} {m.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Bloc payé ou verrouillé */}
      {paid && roadmap ? (
        <RoadmapUnlocked roadmap={roadmap} downloadPDF={downloadPDF} resendPDF={resendPDF} resendStatus={resendStatus} />
      ) : (
        <PaywallCard
          price={price}
          openPayment={openPayment}
          pollingPayment={pollingPayment}
          onRefreshPaid={onRefreshPaid}
          onPromoUnlocked={onRefreshPaid}
        />
      )}

      <div style={{ background: "rgba(31,94,86,0.05)", border: "1px solid rgba(31,94,86,0.15)", padding: "14px 18px", borderRadius: 4, marginTop: 24, fontSize: 12, color: "var(--succ-muted)", lineHeight: 1.55 }}>
        AUPRÉA n'est ni notaire, ni avocat, ni géomètre. Ce diagnostic ne remplace pas une consultation professionnelle : il vous aide à savoir par où commencer et à préparer vos rendez-vous.
      </div>
    </div>
  );
}

function PaywallCard({ price, openPayment, pollingPayment, onRefreshPaid, onPromoUnlocked }) {
  const [showPromo, setShowPromo] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoStatus, setPromoStatus] = useState(""); // "", "loading", "error", "success"
  const [promoMsg, setPromoMsg] = useState("");

  const submitPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoStatus("loading");
    setPromoMsg("");
    try {
      await api.post("/succession/mine/apply-promo", { code: promoCode.trim() });
      setPromoStatus("success");
      setPromoMsg("Code accepté — feuille de route en cours de génération…");
      // Déclenche le polling / rafraîchissement pour charger la roadmap
      if (typeof onPromoUnlocked === "function") await onPromoUnlocked();
    } catch (e) {
      setPromoStatus("error");
      setPromoMsg(formatApiError(e) || "Code invalide.");
    }
  };

  return (
    <div className="succ-card" style={{ position: "relative", overflow: "hidden", background: "linear-gradient(135deg, #fff 0%, rgba(31,94,86,0.04) 100%)" }} data-testid="paywall-card">
      <div style={{ position: "absolute", top: 12, right: 12, background: "var(--succ-brass)", color: "#fff", padding: "3px 12px", borderRadius: 999, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em" }}>{price} €</div>
      <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Feuille de route complète</div>
      <h2 style={{ margin: "0 0 14px 0", fontSize: 22 }}>
        <Lock size={20} style={{ display: "inline", marginRight: 8, verticalAlign: "middle", color: "var(--succ-brass)" }} />
        Débloquez votre feuille de route personnalisée
      </h2>
      <p style={{ fontSize: 15, lineHeight: 1.65, color: "var(--succ-ink)", marginBottom: 16 }}>
        Pour <strong>{price} €</strong>, obtenez :
      </p>
      <ul style={{ paddingLeft: 20, marginBottom: 20, color: "var(--succ-ink)", fontSize: 14, lineHeight: 1.7 }}>
        <li>L'ordre précis des démarches à mener (professionnel par professionnel)</li>
        <li>Le raisonnement personnalisé qui a conduit à chaque recommandation</li>
        <li>La liste des documents à réunir pour chaque rendez-vous</li>
        <li>Les actions concrètes à préparer avant chaque consultation</li>
        <li>La feuille de route envoyée automatiquement <strong>par e-mail au format PDF</strong></li>
      </ul>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <button onClick={openPayment} className="succ-btn-primary" style={{ padding: "14px 30px", fontSize: 15 }} data-testid="btn-pay-stripe">
          Payer {price} € et débloquer →
        </button>
        {pollingPayment && (
          <span style={{ fontSize: 13, color: "var(--succ-teal)" }} data-testid="payment-polling">
            En attente de la confirmation Stripe…
          </span>
        )}
        {!pollingPayment && (
          <button onClick={onRefreshPaid} className="succ-btn-outline" data-testid="btn-check-payment" style={{ fontSize: 13, padding: "8px 14px" }}>
            J'ai payé — vérifier maintenant
          </button>
        )}
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: "var(--succ-muted)" }}>
        Paiement sécurisé via Stripe · Vous serez redirigé(e) vers une page Stripe officielle.
      </div>

      {/* Code promo — discret, entièrement côté serveur */}
      <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px solid rgba(31,94,86,0.1)" }}>
        {!showPromo ? (
          <button
            type="button"
            onClick={() => setShowPromo(true)}
            data-testid="btn-toggle-promo"
            style={{ background: "transparent", border: "none", color: "var(--succ-muted)", fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline", textUnderlineOffset: 3 }}
          >
            J'ai un code promo
          </button>
        ) : (
          <div data-testid="promo-field-wrapper">
            <label style={{ display: "block", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--succ-muted)", marginBottom: 6, fontWeight: 600 }}>
              Code promo
            </label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value); if (promoStatus === "error") { setPromoStatus(""); setPromoMsg(""); } }}
                placeholder="Saisir votre code"
                autoComplete="off"
                data-testid="input-promo-code"
                disabled={promoStatus === "loading" || promoStatus === "success"}
                style={{
                  minWidth: 220, padding: "10px 12px", fontSize: 14,
                  border: "1px solid var(--succ-line)", borderRadius: 4, background: "#fff", color: "var(--succ-ink)",
                }}
                onKeyDown={(e) => { if (e.key === "Enter") submitPromo(); }}
              />
              <button
                type="button"
                onClick={submitPromo}
                disabled={promoStatus === "loading" || promoStatus === "success" || !promoCode.trim()}
                className="succ-btn-outline"
                data-testid="btn-apply-promo"
                style={{ fontSize: 13, padding: "10px 18px", opacity: (promoStatus === "loading" || promoStatus === "success" || !promoCode.trim()) ? 0.6 : 1 }}
              >
                {promoStatus === "loading" ? "Vérification…" : promoStatus === "success" ? "Validé ✓" : "Valider"}
              </button>
              {promoStatus !== "success" && (
                <button
                  type="button"
                  onClick={() => { setShowPromo(false); setPromoCode(""); setPromoStatus(""); setPromoMsg(""); }}
                  data-testid="btn-cancel-promo"
                  style={{ background: "transparent", border: "none", color: "var(--succ-muted)", fontSize: 12, cursor: "pointer", padding: "10px 4px", textDecoration: "underline", textUnderlineOffset: 3 }}
                >
                  Annuler
                </button>
              )}
            </div>
            {promoMsg && (
              <div
                data-testid={`promo-msg-${promoStatus}`}
                style={{
                  marginTop: 8, fontSize: 13,
                  color: promoStatus === "error" ? "#b53a3a" : promoStatus === "success" ? "var(--succ-teal)" : "var(--succ-muted)",
                }}
              >
                {promoMsg}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function RoadmapUnlocked({ roadmap, downloadPDF, resendPDF, resendStatus }) {
  const perPro = roadmap.per_pro || {};
  const order = roadmap.professions_recommended || [];
  return (
    <div data-testid="roadmap-unlocked">
      <div style={{ background: "var(--succ-teal-dark)", color: "#EFF1EC", padding: "20px 24px", borderRadius: 6, marginBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <CheckCircle2 size={20} style={{ color: "var(--succ-brass)" }} />
          <div style={{ color: "var(--succ-brass)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600 }}>Feuille de route débloquée</div>
        </div>
        <h2 style={{ color: "#EFF1EC", margin: "0 0 8px 0", fontSize: 22 }}>Votre feuille de route personnalisée</h2>
        <p style={{ fontSize: 14, color: "rgba(239,241,236,0.85)", margin: 0 }}>
          Les professionnels ci-dessous sont présentés dans l'ordre d'intervention recommandé. Un PDF a été envoyé à votre e-mail.
        </p>
        <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={downloadPDF} data-testid="btn-download-pdf" style={{ background: "var(--succ-brass)", color: "#fff", padding: "10px 20px", border: "none", borderRadius: 4, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <Download size={14} /> Télécharger le PDF
          </button>
          <button onClick={resendPDF} data-testid="btn-resend-pdf" style={{ background: "transparent", color: "#EFF1EC", padding: "10px 20px", border: "1px solid rgba(239,241,236,0.4)", borderRadius: 4, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <Mail size={14} /> Recevoir à nouveau par e-mail
          </button>
        </div>
        {resendStatus && <div style={{ marginTop: 10, fontSize: 12, color: "var(--succ-brass)" }} data-testid="resend-status">{resendStatus}</div>}
      </div>

      {order.map((k, idx) => {
        const p = perPro[k];
        if (!p) return null;
        return (
          <div key={k} className="succ-card mb-4" data-testid={`roadmap-pro-${k}`}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <span style={{ background: "var(--succ-brass)", color: "#fff", width: 26, height: 26, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>{idx + 1}</span>
              <h3 style={{ margin: 0, fontSize: 18 }}>{p.icon} {p.label}</h3>
            </div>
            <p style={{ color: "var(--succ-ink)", fontSize: 14, lineHeight: 1.65, marginBottom: 12 }}>{p.raisonnement}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="succ-label mb-2">Documents à réunir</div>
                <ul style={{ paddingLeft: 20, margin: 0, fontSize: 13, lineHeight: 1.65, color: "var(--succ-ink)" }}>
                  {(p.docs_status || p.docs || []).map((d, i) => {
                    const label = typeof d === "string" ? d : d.label;
                    const owned = typeof d === "object" && d.status === "possession";
                    return (
                      <li key={i} style={{ color: owned ? "var(--succ-teal)" : "var(--succ-ink)" }}>
                        {owned ? "✔ " : "○ "}{label}
                        {owned && <em style={{ color: "var(--succ-teal)", fontSize: 12 }}> (en votre possession)</em>}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div>
                <div className="succ-label mb-2">Actions concrètes</div>
                <ol style={{ paddingLeft: 20, margin: 0, fontSize: 13, lineHeight: 1.65, color: "var(--succ-ink)" }}>
                  {(p.actions || []).map((a, i) => <li key={i}>{a}</li>)}
                </ol>
              </div>
            </div>
          </div>
        );
      })}

      <div className="succ-card" style={{ background: "rgba(173,124,51,0.06)", borderColor: "rgba(173,124,51,0.25)" }}>
        <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>En synthèse</div>
        <p style={{ fontSize: 14, lineHeight: 1.65, margin: 0 }}>
          Le <strong>notaire reste l'interlocuteur central</strong> : il centralise les conclusions des autres professionnels
          et rédige les actes définitifs. L'annuaire des professionnels partenaires d'AUPRÉA et la messagerie interne
          seront prochainement accessibles depuis cet espace.
        </p>
      </div>
    </div>
  );
}

/* ---------- Composants réutilisables ---------- */
export function SectionHeader({ n, title, subtitle }) {
  return (
    <div className="mb-8" data-testid={`section-header-${n}`}>
      <div style={{ color: "var(--succ-brass-dark)", fontSize: 11, letterSpacing: "0.25em", textTransform: "uppercase", fontWeight: 600, marginBottom: 8 }}>Étape {n} sur 4</div>
      <h1 style={{ margin: "0 0 8px 0", fontSize: 30 }}>{title}</h1>
      <p style={{ color: "var(--succ-muted)", fontSize: 15, lineHeight: 1.6, margin: 0 }}>{subtitle}</p>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="succ-card mb-6">
      <h3 style={{ marginTop: 0, marginBottom: 16, fontSize: 15, color: "var(--succ-teal-dark)", borderBottom: "1px solid var(--succ-line)", paddingBottom: 8 }}>{title}</h3>
      {children}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="succ-label">{label}</label>
      {children}
    </div>
  );
}

function RadioLine({ label, value, options, onChange, testid }) {
  return (
    <div className="mt-4" data-testid={testid}>
      <div className="succ-label mb-2">{label}</div>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const sel = value === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              data-testid={`${testid}-${o.v}`}
              style={{
                padding: "8px 16px",
                border: `1px solid ${sel ? "var(--succ-teal)" : "var(--succ-line)"}`,
                background: sel ? "var(--succ-teal)" : "#fff",
                color: sel ? "#EFF1EC" : "var(--succ-ink)",
                borderRadius: 999, fontSize: 13, fontWeight: sel ? 600 : 500,
                cursor: "pointer",
              }}
            >
              {o.lbl}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function EnfantsList({ enfants, setEnfants }) {
  const [draft, setDraft] = useState({ prenom: "", nom: "", statut: "vivant", avait_enfants: "", petits_enfants: [] });
  const add = () => {
    if (!draft.prenom.trim() && !draft.nom.trim()) return;
    setEnfants([...enfants, { ...draft }]);
    setDraft({ prenom: "", nom: "", statut: "vivant", avait_enfants: "", petits_enfants: [] });
  };
  const remove = (idx) => setEnfants(enfants.filter((_, i) => i !== idx));
  const updateEnfant = (idx, patch) => {
    setEnfants(enfants.map((e, i) => i === idx ? { ...e, ...patch } : e));
  };
  const addPetit = (idx) => {
    const p = { prenom: "", nom: "" };
    updateEnfant(idx, { petits_enfants: [...(enfants[idx].petits_enfants || []), p] });
  };
  const updatePetit = (idx, pidx, patch) => {
    const grands = enfants[idx].petits_enfants.map((g, i) => i === pidx ? { ...g, ...patch } : g);
    updateEnfant(idx, { petits_enfants: grands });
  };
  const removePetit = (idx, pidx) => {
    updateEnfant(idx, { petits_enfants: enfants[idx].petits_enfants.filter((_, i) => i !== pidx) });
  };
  return (
    <div className="mt-4" data-testid="enfants-list">
      <div className="succ-label mb-2">Liste des enfants</div>
      {enfants.map((e, i) => (
        <div key={i} style={{ background: "rgba(31,94,86,0.04)", border: "1px solid var(--succ-line)", padding: 12, borderRadius: 4, marginBottom: 8 }} data-testid={`enfant-${i}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <input className="succ-input" placeholder="Prénom" value={e.prenom} onChange={(ev) => updateEnfant(i, { prenom: ev.target.value })} data-testid={`enfant-${i}-prenom`} />
            <input className="succ-input" placeholder="Nom" value={e.nom} onChange={(ev) => updateEnfant(i, { nom: ev.target.value })} data-testid={`enfant-${i}-nom`} />
            <select className="succ-input" value={e.statut} onChange={(ev) => updateEnfant(i, { statut: ev.target.value })} data-testid={`enfant-${i}-statut`}>
              <option value="vivant">Vivant</option>
              <option value="decede">Décédé(e)</option>
            </select>
            <button onClick={() => remove(i)} className="succ-btn-outline" style={{ padding: "8px 12px", color: "#b53a3a", borderColor: "#b53a3a" }} data-testid={`enfant-${i}-remove`}><Trash2 size={14} /></button>
          </div>
          {e.statut === "decede" && (
            <div className="mt-3">
              <RadioLine label="Cet enfant avait-il lui-même des enfants ?" value={e.avait_enfants} options={OUI_NON_INCONNU} onChange={(v) => updateEnfant(i, { avait_enfants: v })} testid={`enfant-${i}-avait-enfants`} />
              {e.avait_enfants === "oui" && (
                <div className="mt-3">
                  <div className="succ-label mb-2">Petits-enfants (par représentation)</div>
                  {(e.petits_enfants || []).map((g, gi) => (
                    <div key={gi} className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2" data-testid={`petit-${i}-${gi}`}>
                      <input className="succ-input" placeholder="Prénom" value={g.prenom} onChange={(ev) => updatePetit(i, gi, { prenom: ev.target.value })} />
                      <input className="succ-input" placeholder="Nom" value={g.nom} onChange={(ev) => updatePetit(i, gi, { nom: ev.target.value })} />
                      <button onClick={() => removePetit(i, gi)} className="succ-btn-outline" style={{ padding: "6px 10px", color: "#b53a3a", borderColor: "#b53a3a" }}><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <button onClick={() => addPetit(i)} className="succ-btn-outline" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`enfant-${i}-add-petit`}>
                    <Plus size={12} style={{ display: "inline", marginRight: 4 }} /> Ajouter un petit-enfant
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <div className="mt-2">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <input className="succ-input" placeholder="Prénom" value={draft.prenom} onChange={(e) => setDraft({ ...draft, prenom: e.target.value })} data-testid="enfant-new-prenom" />
          <input className="succ-input" placeholder="Nom" value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} data-testid="enfant-new-nom" />
          <select className="succ-input" value={draft.statut} onChange={(e) => setDraft({ ...draft, statut: e.target.value })} data-testid="enfant-new-statut">
            <option value="vivant">Vivant</option>
            <option value="decede">Décédé(e)</option>
          </select>
          <button onClick={add} className="succ-btn-primary" style={{ padding: "10px 12px" }} data-testid="enfant-add"><Plus size={14} style={{ display: "inline", marginRight: 4 }} /> Ajouter</button>
        </div>
      </div>
    </div>
  );
}

function HeritiersList({ heritiers, setHeritiers }) {
  const [draft, setDraft] = useState({ nom: "", telephone: "", email: "", adresse: "" });
  const add = () => {
    if (!Object.values(draft).some((v) => v.trim())) return;
    setHeritiers([...heritiers, { ...draft }]);
    setDraft({ nom: "", telephone: "", email: "", adresse: "" });
  };
  const remove = (idx) => setHeritiers(heritiers.filter((_, i) => i !== idx));
  return (
    <div className="mt-3" data-testid="heritiers-list">
      <div className="succ-label mb-2">Coordonnées des héritiers (un seul champ suffit)</div>
      {heritiers.map((h, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(31,94,86,0.04)", padding: 10, borderRadius: 4, marginBottom: 6, fontSize: 13 }} data-testid={`heritier-${i}`}>
          <span style={{ flex: 1 }}><strong>{h.nom || "—"}</strong>{h.telephone ? " · " + h.telephone : ""}{h.email ? " · " + h.email : ""}{h.adresse ? " · " + h.adresse : ""}</span>
          <button onClick={() => remove(i)} style={{ background: "none", border: "none", color: "#b53a3a", cursor: "pointer" }}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <input className="succ-input" placeholder="Nom" value={draft.nom} onChange={(e) => setDraft({ ...draft, nom: e.target.value })} data-testid="heritier-new-nom" />
        <input className="succ-input" placeholder="Téléphone" value={draft.telephone} onChange={(e) => setDraft({ ...draft, telephone: e.target.value })} data-testid="heritier-new-tel" />
        <input className="succ-input" placeholder="Email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} data-testid="heritier-new-email" />
        <div style={{ display: "flex", gap: 6 }}>
          <input className="succ-input" placeholder="Adresse" value={draft.adresse} onChange={(e) => setDraft({ ...draft, adresse: e.target.value })} data-testid="heritier-new-adresse" style={{ flex: 1 }} />
          <button onClick={add} className="succ-btn-primary" style={{ padding: "10px 12px" }} data-testid="heritier-add"><Plus size={14} /></button>
        </div>
      </div>
    </div>
  );
}

function BiensList({ biens, setBiens }) {
  const [draft, setDraft] = useState({ type: "", ville: "", num_lot: "" });
  const add = () => {
    if (!draft.type && !draft.ville) return;
    setBiens([...biens, { ...draft }]);
    setDraft({ type: "", ville: "", num_lot: "" });
  };
  const remove = (idx) => setBiens(biens.filter((_, i) => i !== idx));
  return (
    <div className="mt-3" data-testid="biens-list">
      <div className="succ-label mb-2">Biens immobiliers déclarés</div>
      {biens.map((b, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(31,94,86,0.04)", padding: 10, borderRadius: 4, marginBottom: 6, fontSize: 13 }} data-testid={`bien-${i}`}>
          <span style={{ flex: 1 }}><strong>{b.type || "—"}</strong>{b.ville ? " · " + b.ville : ""}{b.num_lot ? " · lot " + b.num_lot : ""}</span>
          <button onClick={() => remove(i)} style={{ background: "none", border: "none", color: "#b53a3a", cursor: "pointer" }}><Trash2 size={14} /></button>
        </div>
      ))}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
        <select className="succ-input" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} data-testid="bien-new-type">
          <option value="">— Type —</option>
          {TYPES_BIENS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className="succ-input" placeholder="Ville" value={draft.ville} onChange={(e) => setDraft({ ...draft, ville: e.target.value })} data-testid="bien-new-ville" />
        <input className="succ-input" placeholder="N° de lot (terrain)" value={draft.num_lot} onChange={(e) => setDraft({ ...draft, num_lot: e.target.value })} data-testid="bien-new-lot" />
        <button onClick={add} className="succ-btn-primary" style={{ padding: "10px 12px" }} data-testid="bien-add"><Plus size={14} style={{ display: "inline", marginRight: 4 }} /> Ajouter</button>
      </div>
    </div>
  );
}

function MotifsCheckboxes({ motifs, selected, onChange }) {
  const toggle = (k) => {
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k]);
  };
  return (
    <div className="mt-3" data-testid="motifs-list">
      <div className="succ-label mb-2">Sélectionnez tous les motifs qui s'appliquent</div>
      <div className="grid grid-cols-1 gap-2">
        {motifs.map((m) => {
          const sel = selected.includes(m.key);
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => toggle(m.key)}
              data-testid={`motif-${m.key}`}
              style={{
                display: "flex", alignItems: "flex-start", gap: 10,
                padding: "10px 14px",
                border: `1px solid ${sel ? "var(--succ-teal)" : "var(--succ-line)"}`,
                background: sel ? "rgba(31,94,86,0.08)" : "#fff",
                color: sel ? "var(--succ-teal-dark)" : "var(--succ-ink)",
                borderRadius: 4, fontSize: 13, cursor: "pointer",
                textAlign: "left", fontWeight: sel ? 600 : 500,
              }}
            >
              <span style={{ width: 18, height: 18, border: `1px solid ${sel ? "var(--succ-teal)" : "var(--succ-line)"}`, borderRadius: 3, display: "inline-flex", alignItems: "center", justifyContent: "center", background: sel ? "var(--succ-teal)" : "#fff", color: "#fff", flexShrink: 0, marginTop: 1 }}>
                {sel ? "✓" : ""}
              </span>
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
